import { useEffect, useState, type ReactNode } from 'react';
import { currentSession, useSession } from './session';
import { ReceiptContactContext, type ReceiptContact } from './receipt-contact-context';
const emptyContact = (): ReceiptContact => ({ email: '', firstName: '', lastName: '' });

/** Route-independent contact data stays in memory and belongs to one session identity. */
export function ReceiptContactProvider({ children }: { children: ReactNode }): JSX.Element {
  const session = useSession();
  const identity = `${session.profile?.user.id ?? 'guest'}:${session.epoch}`;
  const [saved, setSaved] = useState(() => ({ identity, contact: emptyContact() }));
  useEffect(() => {
    setSaved((previous) =>
      previous.identity === identity ? previous : { identity, contact: emptyContact() },
    );
  }, [identity]);
  useEffect(() => {
    const clear = (): void => setSaved({ identity, contact: emptyContact() });
    window.addEventListener('giving:identity-changed', clear);
    return () => window.removeEventListener('giving:identity-changed', clear);
  }, [identity]);
  return (
    <ReceiptContactContext.Provider
      value={{
        contact: saved.identity === identity ? saved.contact : emptyContact(),
        updateContact: (patch) => {
          const current = currentSession();
          if (`${current.profile?.user.id ?? 'guest'}:${current.epoch}` !== identity) return;
          setSaved((previous) => ({
            identity,
            contact: {
              ...(previous.identity === identity ? previous.contact : emptyContact()),
              ...patch,
            },
          }));
        },
      }}
    >
      {children}
    </ReceiptContactContext.Provider>
  );
}
